import { useEffect, useState } from "react";
import { Download, WifiOff, X } from "lucide-react";

export default function PwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("rigcalc_pwa_dismissed") === "1");
  const [installed, setInstalled] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    // Detect iOS Safari (no beforeinstallprompt) & not already standalone
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isIos && !standalone && !dismissed) setShowIos(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [dismissed]);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  const dismiss = () => {
    setDismissed(true);
    setShowIos(false);
    localStorage.setItem("rigcalc_pwa_dismissed", "1");
  };

  return (
    <>
      {!online && (
        <div
          data-testid="offline-banner"
          className="fixed top-0 inset-x-0 z-50 bg-[#f59e0b] text-[#0b0e13] py-1.5 text-xs mono tracking-widest text-center font-bold flex items-center justify-center gap-2"
        >
          <WifiOff className="w-3.5 h-3.5" />
          OFFLINE — CALCULATORS + SPECS STILL WORKING
        </div>
      )}

      {!installed && !dismissed && (deferred || showIos) && (
        <div
          data-testid="pwa-install-banner"
          className="fixed bottom-4 right-4 z-40 max-w-sm border border-[#f59e0b] bg-[#11151c] shadow-2xl p-4"
        >
          <button
            data-testid="pwa-dismiss-btn"
            onClick={dismiss}
            className="absolute top-2 right-2 text-[#94a3b8] hover:text-[#f8fafc]"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-3 pr-6">
            <div className="w-10 h-10 bg-[#f59e0b] flex items-center justify-center shrink-0">
              <Download className="w-5 h-5 text-[#0b0e13]" />
            </div>
            <div>
              <div className="heading text-sm font-bold mb-1">Install RIGCALC</div>
              {deferred ? (
                <p className="text-xs mono text-[#94a3b8] mb-3">
                  Add to your home screen for offline calcs and instant launch.
                </p>
              ) : (
                <p className="text-xs mono text-[#94a3b8] mb-3">
                  iOS: tap <span className="text-[#f59e0b]">Share</span> → <span className="text-[#f59e0b]">Add to Home Screen</span>.
                </p>
              )}
              {deferred && (
                <button
                  data-testid="pwa-install-btn"
                  onClick={install}
                  className="bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-3 py-1.5 mono text-xs tracking-widest uppercase font-bold"
                >
                  Install App
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
